import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators, ReactiveFormsModule, FormsModule } from '@angular/forms';
import { UserService } from '../../common/services/user.service';
import { AssociationService } from '../../common/services/association.service';
import { StorageService } from '../../common/services/storage.service';
import { UpdateCurrentUser, CurrentUser } from '../../common/models/user';
import { Association, UpdateAssociation } from '../../common/models/association';
import { MatSnackBar, MatSnackBarConfig } from '@angular/material/snack-bar';
import { CustomizerSettingsService } from '../../customizer-settings/customizer-settings.service';


import { MatFormFieldModule } from '@angular/material/form-field';
import { MatStepperModule } from '@angular/material/stepper';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatSelectModule } from '@angular/material/select';
import { FileUploadModule } from '@iplab/ngx-file-upload';

import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import { Subject, takeUntil } from 'rxjs';

@Component({
  selector: 'app-account-settings',
  templateUrl: './account-settings.component.html',
  styleUrls: ['./account-settings.component.scss'],
  standalone: true,
  imports: [
    ReactiveFormsModule,
    FormsModule,
    MatCardModule,
    MatStepperModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatDatepickerModule,
    MatSelectModule,
    FileUploadModule
]
})
export class AccountSettingsComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();
  
  isHandset: boolean = false;

  public multiple = false;
  isToggled = false;
  
  userForm: FormGroup;
  associationBasicInfoForm: FormGroup;
  associationHeadquartersForm: FormGroup;
  associationDetailsForm: FormGroup;

  user: CurrentUser | null = null;
  isAssociationUser = false;

  private readonly snackbarConfig: MatSnackBarConfig = {
    duration: 3000,
    horizontalPosition: 'right',
    verticalPosition: 'top'
  };


  constructor(
    private fb: FormBuilder,
    private userService: UserService,
    private associationService: AssociationService,
    private storageService: StorageService,
    private snackBar: MatSnackBar,
    public themeService: CustomizerSettingsService,
    private breakpointObserver: BreakpointObserver
  ) {

    // User form
    this.userForm = this.fb.group({
      firstName: ['', Validators.required],
      lastName: ['', Validators.required],
      email: [{ value: '', disabled: true }, [Validators.required, Validators.email]]
    });

    // Association forms
    this.associationBasicInfoForm = this.fb.group({
      name: [null, Validators.required],
      email: [{ value: null, disabled: true }, [Validators.required, Validators.email]],
      oib: [{ value: null, disabled: true }, Validators.required],
      rno: [{ value: null, disabled: true }, Validators.required],
      yearOfEstablishment: [null, Validators.required],
      dateOfRegistration: [null, Validators.required],
      registrationNumber: [{value: null, disabled: true }, Validators.required],
      registeredAuthority: [null, Validators.required],
      bankName: [null, Validators.required],
      bankAccountNumber: [null, Validators.required],
      contactNumber: [null, Validators.required],
      web: [null]
    });

    this.associationHeadquartersForm = this.fb.group({
      county: [null, Validators.required],
      postalCodeAndHeadquarters: [null, Validators.required],
      address: [null, Validators.required]
    });

    this.associationDetailsForm = this.fb.group({
      objectivesAccordingToStatute: [null, Validators.required],
      purposeAndFieldOfOperation: [null, Validators.required],
      activitiesAccordingToStatute: [null, Validators.required]
    });

    // Theme toggle subscription
    this.themeService.isToggled$.pipe(takeUntil(this.destroy$)).subscribe(isToggled => {
      this.isToggled = isToggled;
    });
  }

  ngOnInit(): void {
    this.breakpointObserver.observe([Breakpoints.XSmall, Breakpoints.Small]).pipe(takeUntil(this.destroy$))
    .subscribe(result => {
      this.isHandset = result.matches;
    });


    this.user = this.storageService.getUser();
    if (this.user) {
      this.populateUserForm(this.user);
      this.isAssociationUser = this.user.isAssociationResponsiblePerson();
      if (this.isAssociationUser && this.user.associationId) {
        this.populateAssociationForms(this.user.associationId);
      }
    }
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private populateUserForm(user: CurrentUser): void {
    this.userForm.patchValue({
      email: user.email,
      firstName: user.firstName,
      lastName: user.lastName
    });
  }

  private populateAssociationForms(associationId: string): void {
    this.associationService.getAssociation(associationId).pipe(takeUntil(this.destroy$)).subscribe({
      next: (association: Association) => {
        // Basic info
        this.associationBasicInfoForm.patchValue({
          name: association.name,
          email: association.email,
          oib: association.oib,
          rno: association.rno,
          yearOfEstablishment: association.yearOfEstablishment,
          dateOfRegistration: association.dateOfRegistration,
          registrationNumber: association.registrationNumber,
          registeredAuthority: association.registeredAuthority,
          bankName: association.bankName,
          bankAccountNumber: association.bankAccountNumber,
          contactNumber: association.contactNumber,
          web: association.web
        });

        // Headquarters
        this.associationHeadquartersForm.patchValue({
          county: association.county,
          postalCodeAndHeadquarters: association.postalCodeAndHeadquarters,
          address: association.address
        });

        // Details
        this.associationDetailsForm.patchValue({
          objectivesAccordingToStatute: association.objectivesAccordingToStatute,
          purposeAndFieldOfOperation: association.purposeAndFieldOfOperation,
          activitiesAccordingToStatute: association.activitiesAccordingToStatute
        });
      },
      error: () => {
        this.snackBar.open('Greška prilikom učitavanja podataka udruge.', 'Zatvori', this.snackbarConfig);
      }
    });
  }

  saveProfile(): void {
    if (this.user && this.userForm.valid) {
      const profileData: UpdateCurrentUser = {
        firstName: this.userForm.value.firstName,
        lastName: this.userForm.value.lastName
      };

      this.userService.saveCurrentUser(profileData).pipe(takeUntil(this.destroy$)).subscribe({
        next: () => {
          this.user = { ...this.user!, ...profileData } as CurrentUser;
          this.storageService.saveUser(this.user!);
          this.snackBar.open('Korisnički podaci su uspješno ažurirani.', 'Zatvori', this.snackbarConfig);
        },
        error: () => {
          this.snackBar.open('Dogodila se pogreška prilikom ažuriranja korisničkih podataka.', 'Zatvori', this.snackbarConfig);
        }
      });
    }

    if (this.isAssociationUser &&
        this.associationBasicInfoForm.valid &&
        this.associationHeadquartersForm.valid &&
        this.associationDetailsForm.valid &&
        this.user?.associationId) {
      const basicInfoValues = this.associationBasicInfoForm.value;
      const headquartersValues = this.associationHeadquartersForm.value;
      const detailsValues = this.associationDetailsForm.value;

      const associationData: UpdateAssociation = {
        id: this.user.associationId,
        name: basicInfoValues.name,
        yearOfEstablishment: basicInfoValues.yearOfEstablishment,
        dateOfRegistration: basicInfoValues.dateOfRegistration,
        registeredAuthority: basicInfoValues.registeredAuthority,
        bankName: basicInfoValues.bankName,
        bankAccountNumber: basicInfoValues.bankAccountNumber,
        contactNumber: basicInfoValues.contactNumber,
        web: basicInfoValues.web,
        county: headquartersValues.county,
        postalCodeAndHeadquarters: headquartersValues.postalCodeAndHeadquarters,
        address: headquartersValues.address,
        objectivesAccordingToStatute: detailsValues.objectivesAccordingToStatute,
        purposeAndFieldOfOperation: detailsValues.purposeAndFieldOfOperation,
        activitiesAccordingToStatute: detailsValues.activitiesAccordingToStatute
      };

      this.associationService.updateAssociation(associationData).pipe(takeUntil(this.destroy$)).subscribe({
        next: () => {
          this.snackBar.open('Podaci udruge su uspješno ažurirani.', 'Zatvori', this.snackbarConfig);
        },
        error: () => {
          this.snackBar.open('Dogodila se pogreška prilikom ažuriranja podataka udruge.', 'Zatvori', this.snackbarConfig);
        }
      });
    }
  }
}
